/**
 * InputBuffer - manages client-side input history for prediction and reconciliation.
 * Stores inputs with sequence numbers and timestamps in a circular buffer.
 */

export interface BufferedInput {
  /** Monotonically increasing sequence number */
  sequence: number;
  /** Timestamp when input was created (client time) */
  timestamp: number;
  /** Input data (forward, right, jump, etc.) */
  input: {
    forward?: number;
    right?: number;
    jump?: boolean;
    [key: string]: any;
  };
}

/**
 * Circular buffer for storing client inputs with sequence numbers
 */
export class InputBuffer {
  private buffer: BufferedInput[] = [];
  private maxSize: number;
  private nextSequence: number = 0;

  /**
   * Creates a new input buffer
   * @param maxSize Maximum number of inputs to store (default: 128)
   */
  constructor(maxSize: number = 128) {
    this.maxSize = maxSize;
  }

  /**
   * Adds a new input to the buffer
   * @param input The input data to store
   * @returns The sequence number assigned to this input
   */
  public addInput(input: { forward?: number; right?: number; jump?: boolean; [key: string]: any }): number {
    const sequence = this.nextSequence++;
    const bufferedInput: BufferedInput = {
      sequence,
      timestamp: Date.now(),
      input: { ...input }
    };

    // Add to buffer
    this.buffer.push(bufferedInput);

    // Remove oldest if buffer is full
    if (this.buffer.length > this.maxSize) {
      this.buffer.shift();
    }

    return sequence;
  }

  /**
   * Removes all inputs up to and including the specified sequence number
   * @param lastProcessedSequence The last sequence number processed by the server
   */
  public acknowledgeInputs(lastProcessedSequence: number): void {
    this.buffer = this.buffer.filter(input => input.sequence > lastProcessedSequence);
  }

  /**
   * Gets all unacknowledged inputs (for replaying after server correction)
   * @returns Array of buffered inputs
   */
  public getUnacknowledgedInputs(): BufferedInput[] {
    return [...this.buffer];
  }

  /**
   * Gets a specific input by sequence number
   * @param sequence The sequence number to find
   * @returns The buffered input, or undefined if not found
   */
  public getInput(sequence: number): BufferedInput | undefined {
    return this.buffer.find(input => input.sequence === sequence);
  }

  /**
   * Gets the current sequence number (next to be assigned)
   * @returns The next sequence number
   */
  public getCurrentSequence(): number {
    return this.nextSequence;
  }

  /**
   * Gets the number of buffered inputs
   * @returns The buffer size
   */
  public getSize(): number {
    return this.buffer.length;
  }

  /**
   * Clears all buffered inputs
   */
  public clear(): void {
    this.buffer = [];
  }

  /**
   * Gets the oldest buffered input
   * @returns The oldest input, or undefined if buffer is empty
   */
  public getOldest(): BufferedInput | undefined {
    return this.buffer[0];
  }

  /**
   * Gets the newest buffered input
   * @returns The newest input, or undefined if buffer is empty
   */
  public getNewest(): BufferedInput | undefined {
    return this.buffer[this.buffer.length - 1];
  }
}

